import { defineStore } from 'pinia';
import { api } from '@/utils/api';
import Cookies from 'js-cookie';
import { useAuthStore } from '@/store/AuthStore';

function safeParse(raw) {
    try {
        return JSON.parse(raw);
    } catch (e) {
        return { type: 'answer', message: raw };
    }
}

function processMessages(content) {
    if (!Array.isArray(content)) return [];

    console.log('[processMessages] raw from backend:', content.map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content.substring(0, 150) : m.content
    })));

    const filtered = content
        .filter(msg => msg.role !== 'system')
        .map(msg => {
            const role = msg.role === 'user' ? 'user' : 'assistant';

            if (role === 'assistant') {
                let raw = msg.content;
                if (typeof raw !== 'string') return msg;
                const match = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
                if (match) raw = match[1].trim();
                msg.content = safeParse(raw);

                if (msg.content?.actionId && msg.content?.options) {
                    msg.typeChunk = 'BUTTONS';
                } else if (msg.content?.action === 'get_files_by_ids') {
                    msg.typeChunk = 'FILES_RESULT';
                } else if (msg.content?.action) {
                    msg.typeChunk = 'CONFIRMATION_REQUIRED';
                } else if (msg.content?.type === 'answer') {
                    msg.typeChunk = 'FINAL';
                }
            }

            if (role === 'user' && typeof msg.content === 'string') {
                const parsed = safeParse(msg.content);
                if (parsed && typeof parsed === 'object' && 'success' in parsed && 'message' in parsed) {
                    msg.content = parsed;
                    msg.isToolResult = true;
                    if (parsed.action === 'get_files_by_ids') {
                        msg.typeChunk = 'FILES_RESULT';
                    } else {
                        msg.typeChunk = 'TOOL';
                    }
                }
            }

            return msg;
        });

    for (let i = 1; i < filtered.length; i++) {
        if (filtered[i].role === 'user' && filtered[i - 1].typeChunk === 'BUTTONS') {
            filtered[i].isButtonChoice = true;
        }
    }

    console.log('[processedMessages] raw from backend:', filtered.map(m => ({
        typeChunk: m.typeChunk,
        role: m.role,
        content: typeof m.content === 'string' ? m.content.substring(0, 150) : m.content
    })));

    return filtered;
}

export const useAgentStore = defineStore('agent', {
    state: () => ({
        messages: [],
        messagesPage: 0,
        messagesTotalPages: 0,
        sessions: [],
        sessionId: null,
        isLoading: false,
        isStreaming: false,
        error: null,
        isConfirmation: false,
        tokenUsage: null,
        tokenHistory: []
    }),

    getters: {
        hasMoreMessages: (state) => state.messagesPage > 0,
    },

    actions: {
        async fetchSessions(page = 0, size = 10) {
            this.isLoading = true;
            try {
                const response = await api.get('/agent/sessions', { params: { page, size } });
                // Handle Page<SessionDto>
                console.log(response.data);
                if (response.data && response.data.content) {
                    if (page > 0) {
                        this.sessions.content.push(...response.data.content);
                        this.sessions.totalElements = response.data.totalElements;
                        this.sessions.totalPages = response.data.totalPages;
                        this.sessions.number = response.data.number;
                    } else {
                        this.sessions = response.data;
                    }
                } else {
                    this.sessions = response.data || [];
                }
                return this.sessions.content;
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        },

        async createSession(chatMessageId = null) {
            this.error = null;
            this.isLoading = true;
            try {
                // Returns string message "Новая сессия создана"
                const params = chatMessageId ? { chatMessageId: chatMessageId } : {};
                await api.post('/agent/sessions', null, { params });

                // Fetch updated list to find the new session
                // Assuming the new session appears at the top or we need to sort
                const sessions = await this.fetchSessions();

                if (sessions && sessions.length > 0) {
                    // Assuming the backend returns them sorted by createdAt desc, 
                    // or we take the first one as the new one. 
                    // Ideally backend returns the created object, but here we work with what we have.
                    const newSession = sessions[0];
                    this.sessionId = newSession.id;
                    this.messages = [];
                    return newSession;
                }
            } catch (error) {
                console.error(error);
                this.error = 'Ошибка создания чата';
            } finally {
                this.isLoading = false;
            }
        },

        async deleteSession(id) {
            this.isLoading = true;
            try {
                await api.delete(`/agent/sessions/${id}`);
                const sessions = await this.fetchSessions();

                if (sessions && sessions.length > 0) {
                    // Assuming the backend returns them sorted by createdAt desc, 
                    // or we take the first one as the new one. 
                    // Ideally backend returns the created object, but here we work with what we have.
                    const newSession = sessions[0];
                    this.sessionId = newSession.id;
                    this.messages = [];
                    this.selectSession(newSession.id);
                }
            } catch (error) {
                console.error(error);
                this.error = 'Ошибка удаления чата';
            } finally {
                this.isLoading = false;
            }
        },

        async selectSession(id) {
            this.sessionId = id;
            this.error = null;
            this.isLoading = true;
            this.messages = [];
            this.messagesPage = 0;
            this.messagesTotalPages = 0;
            try {
                // Первый запрос — узнаём totalPages
                const firstResponse = await api.get(`/agent/sessions/${id}`, { params: { page: 0, size: 15 } });
                const totalPages = firstResponse.data.totalPages || 1;
                this.messagesTotalPages = totalPages;
                console.debug('First response', firstResponse);

                const lastPage = totalPages - 1;

                let content;
                if (lastPage > 0) {
                    // Загружаем последнюю страницу (самые свежие сообщения)
                    const lastResponse = await api.get(`/agent/sessions/${id}`, { params: { page: lastPage, size: 15 } });
                    content = lastResponse.data.content;
                    this.messagesPage = lastPage;
                    console.debug('Last response', lastResponse);

                    // Если последняя страница неполная — догружаем предыдущую
                    if (content.length < 15 && lastPage > 0) {
                        const prevResponse = await api.get(`/agent/sessions/${id}`, { params: { page: lastPage - 1, size: 15 } });
                        content = [...prevResponse.data.content, ...content];
                        this.messagesPage = lastPage - 1;
                    }
                } else {
                    content = firstResponse.data.content;
                    this.messagesPage = 0;
                }

                this.messages = processMessages(content);
            } catch (error) {
                console.error(error);
                this.error = 'Ошибка загрузки сообщений';
            } finally {
                this.isLoading = false;
            }
        },

        async loadMoreMessages(id) {
            if (this.messagesPage <= 0 || this.isLoading) return false;
            this.isLoading = true;
            try {
                const prevPage = this.messagesPage - 1;
                const response = await api.get(`/agent/sessions/${id}`, { params: { page: prevPage, size: 15 } });
                const older = processMessages(response.data.content);
                this.messages = [...older, ...this.messages];
                this.messagesPage = prevPage;
                return true;
            } catch (error) {
                console.error(error);
                return false;
            } finally {
                this.isLoading = false;
            }
        },

        async sendMessage(message) {

            this.isStreaming = true;
            this.error = null;

            try {

                this.messages.push({
                    role: 'user',
                    content: message
                });

                const data = {
                    sessionId: this.sessionId,
                    message
                };

                const doFetch = async (token) => fetch(
                    `${api.defaults.baseURL}/agent/ask/stream`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(data)
                    }
                );

                let token = Cookies.get('access_token');
                let response = await doFetch(token);

                if (response.status === 401) {
                    const authStore = useAuthStore();
                    const newToken = await authStore.refreshToken();
                    if (newToken) {
                        response = await doFetch(newToken);
                    }
                }

                if (!response.ok) {
                    this.error = `Ошибка сервера: ${response.status}`;
                    this.isStreaming = false;
                    return;
                }

                await this.processResponse(response);

                console.log(this.messages);

            } catch (error) {

                console.error(error);

                this.error = error.message;
                this.isStreaming = false;

            }

        },


        async confirmAction(id) {
            this.isStreaming = true;
            try {
                this.isConfirmation = false;
                const response = await api.post(`/agent/actions/${id}/confirm`);
                console.log(response.data);
                //  this.processResponse(response);
            } catch (error) {
                console.error(error);
            }
        },

        async rejectAction(id) {
            this.isStreaming = true;
            try {
                this.isConfirmation = false;
                const response = await api.post(`/agent/actions/${id}/reject`);
                console.log(response.data);
                //  this.processResponse(response);
            } catch (error) {
                console.error(error);
            }
        },

        async addRating(messageId, rating, maxRating = 5) {
            try {
                const normalized = maxRating > 0 ? rating / maxRating : 0;
                console.log('Adding rating', { messageId, rating: normalized });
                await api.post(`/agent/rating`, { message_id: messageId, rating: normalized });
            } catch (error) {
                console.error(error);
            }
        },

        async sendButtonChoice(actionId, value, label) {
            this.isStreaming = true;
            this.error = null;

            try {
                // Добавляем сообщение с пометкой что это выбор кнопки
                this.messages.push({
                    role: 'user',
                    content: label,
                    isButtonChoice: true,
                    buttonActionId: actionId,
                    buttonValue: value
                });

                const data = {
                    sessionId: this.sessionId,
                    message: label
                };

                const token = Cookies.get('access_token');

                const response = await fetch(
                    `${api.defaults.baseURL}/agent/ask/stream`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(data)
                    }
                );

                this.processResponse(response);
            } catch (error) {
                console.error(error);
                this.error = error.message;
                this.isStreaming = false;
            }
        },

        async fetchTokenUsage() {
            try {
                const response = await api.get('/agent/tokens/usage');
                this.tokenUsage = response.data;
            } catch (error) {
                console.error(error);
            }
        },

        async fetchTokenHistory(days = 30) {
            try {
                const response = await api.get('/agent/tokens/history', { params: { days } });
                this.tokenHistory = response.data;
            } catch (error) {
                console.error(error);
            }
        },

        async processResponse(response) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let isEnabled = true;

            let buffer = '';
            try {

                // eslint-disable-next-line no-constant-condition
                while (isEnabled) {

                    const { value, done } = await reader.read();

                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    const parts = buffer.split('\n\n');

                    buffer = parts.pop();

                    for (const part of parts) {

                        let event = '';
                        let data = '';

                        for (const line of part.split('\n')) {

                            if (line.startsWith('event:'))
                                event = line.replace('event:', '').trim();

                            if (line.startsWith('data:'))
                                data += line.replace('data:', '').trim();

                        }

                        if (!data) continue;

                        console.log('EVENT:', event, data);

                        let agentChunk;
                        try {
                            agentChunk = JSON.parse(data);
                        } catch (e) {
                            console.error('Failed to parse SSE data:', e, data);
                            continue;
                        }
                        console.log(agentChunk);

                        if (event === 'LLM') {
                            this.messages.push({
                                role: 'assistant',
                                typeChunk: agentChunk.typeChunk,
                                content: agentChunk.content
                            });
                        }

                        if (event === 'CONFIRMATION_REQUIRED') {
                            this.isConfirmation = true;
                            this.isStreaming = false;
                            this.messages.push({
                                role: 'assistant',
                                pendingId: agentChunk.pendingId,
                                typeChunk: agentChunk.typeChunk,
                                content: agentChunk.content
                            });
                        }

                        if (event === 'AUTO_ACTION') {
                            this.messages.push({
                                role: 'assistant',
                                typeChunk: 'AUTO_ACTION',
                                content: agentChunk.content
                            });
                        }

                        if (event === 'TOOL') {
                            // агент может слать либо {typeChunk, content: {...}} либо плоский объект
                            const toolContent = agentChunk.content ?? agentChunk;
                            console.log('[TOOL chunk]', toolContent);
                            this.messages.push({
                                role: 'system',
                                typeChunk: toolContent?.action === 'get_files_by_ids' ? 'FILES_RESULT' : 'TOOL',
                                isToolResult: true,
                                content: toolContent
                            });
                        }

                        if (event === 'BUTTONS') {
                            this.messages.push({
                                role: 'assistant',
                                typeChunk: agentChunk.typeChunk,
                                content: agentChunk.content
                            });
                            this.isStreaming = false;
                        }

                        if (event === 'FINAL') {
                            this.messages.push({
                                role: 'assistant',
                                typeChunk: agentChunk.typeChunk,
                                content: agentChunk.content
                            });
                            isEnabled = false;
                            this.isStreaming = false;
                        }

                    }

                    this.messages = [...this.messages];

                }

                console.log('Stream finished');
            } catch (error) {
                console.error('Stream error:', error);
                this.error = error.message;
            } finally {
                this.isStreaming = false;
            }
        }
    }
});
